/**
 * Utility functions for Left Navigation - Functional Pipeline with Type Guards
 */

import { STEP_MAPPING } from '../constants/CuaOnboardintConstants';
import type { ConfigurationItem, NameToLabelResult } from '../data-layer/types';
import type { OnboardingStepsCategoryResponse } from '../data-store/copilotApi';

// Create inverted mapping from shared STEP_MAPPING: stepName -> displayName
const STEP_TO_LABEL_MAP = Object.fromEntries(
  Object.entries(STEP_MAPPING).map(([displayName, stepName]) => [
    stepName,
    displayName,
  ])
);

// Type Guards
const isValidData = (
  data: unknown
): data is OnboardingStepsCategoryResponse | unknown[] => data != null;

const isArrayResponse = (
  data: OnboardingStepsCategoryResponse | unknown[]
): data is unknown[] => Array.isArray(data);

const hasConfiguration = (
  item: unknown
): item is { Configuration: ConfigurationItem[] } =>
  typeof item === 'object' && item !== null && 'Configuration' in item;

const isValidConfigItem = (item: unknown): item is ConfigurationItem =>
  typeof item === 'object' &&
  item !== null &&
  'Name' in item &&
  typeof (item as ConfigurationItem).Name === 'string';

const hasSubcategories = (item: ConfigurationItem): boolean =>
  Array.isArray(item.subcategories) && item.subcategories.length > 0;

// Functional Pipeline
const extractConfiguration = (
  data: OnboardingStepsCategoryResponse | unknown[]
): ConfigurationItem[] => {
  if (isArrayResponse(data)) {
    const firstItem = data[0];

    return hasConfiguration(firstItem) ? firstItem.Configuration : [];
  }

  return hasConfiguration(data) ? data.Configuration : [];
};

const processConfigItems = (configuration: ConfigurationItem[]) => {
  const mainCategories: string[] = [];
  const allSteps: string[] = [];
  const navNames: string[] = [];

  configuration.filter(isValidConfigItem).forEach((item) => {
    mainCategories.push(item.Name);
    allSteps.push(item.Name);

    if (hasSubcategories(item)) {
      const subNames = item
        .subcategories!.filter(
          (sub): sub is { Name: string } => typeof sub.Name === 'string'
        )
        .map((sub) => sub.Name);

      navNames.push(...subNames);
      allSteps.push(...subNames);
    } else {
      navNames.push(item.Name);
    }
  });

  return { mainCategories, allSteps, navNames };
};

const createNameToLabelMap = (names: string[]): Record<string, string> =>
  Object.fromEntries(
    names.map((name) => [name, STEP_TO_LABEL_MAP[name] ?? name])
  );

/**
 * Parse API response using functional pipeline with type guards
 */
const parseOnboardingSteps = (
  data: OnboardingStepsCategoryResponse | undefined
): NameToLabelResult => {
  if (!isValidData(data)) {
    return {
      leftNavLabels: [],
      nameToLabelMap: {},
      mainCategories: [],
      allSteps: [],
    };
  }

  const configuration = extractConfiguration(data);
  const { mainCategories, allSteps, navNames } =
    processConfigItems(configuration);
  const leftNavLabels = navNames.length > 0 ? navNames : mainCategories;
  const nameToLabelMap = createNameToLabelMap(leftNavLabels);

  return { leftNavLabels, nameToLabelMap, mainCategories, allSteps };
};

export default parseOnboardingSteps;
